import { getMexicoBounds, toGoogleBounds } from "@/hooks/useBuscadorHook";

// Resolves a free-text location query to its first Google Places
// prediction via AutocompleteService → PlacesService.getDetails. Shared
// by both LocationInput surfaces (homepage's BuscadorDesktop + Buscar's
// TopBar) so the Enter-without-picking flow produces identical results
// regardless of where the user typed.
//
// Returns the resolved PlaceResult (with geometry + address_components +
// name + formatted_address), or null on any failure — no predictions,
// API error, missing geometry. Callers soft-fall-back to raw-text
// behavior in the null case.
//
// PlacesService requires a DOM element to attach to; a detached div is
// fine since we only need the .getDetails callback.
//
// Optional `{ biasBounds }`: a plain {south, west, north, east} bbox that
// replaces the Mexico-wide bias, mirroring useLeadingMaps' option so the
// Enter path ranks the same way as the dropdown the user just saw.
export const resolveFirstPrediction = (input, { biasBounds = null } = {}) =>
    new Promise((resolve) => {
        if (typeof window === "undefined") {
            resolve(null);
            return;
        }
        const trimmed = typeof input === "string" ? input.trim() : "";
        const gm = window.google?.maps;
        if (!trimmed || !gm?.places) {
            resolve(null);
            return;
        }
        const ac = new gm.places.AutocompleteService();
        // Mirror the dropdown's Autocomplete bias (useBuscadorHook):
        // Mexico bbox `bounds` (soft preference, not strict filter) +
        // geocode/establishment types. Without these, getPlacePredictions
        // falls back to global IP-biased results and diverges from the
        // dropdown the user sees.
        ac.getPlacePredictions(
            {
                input: trimmed,
                bounds: biasBounds ? toGoogleBounds(biasBounds) : getMexicoBounds(),
                types: ["geocode", "establishment"],
            },
            (preds, status) => {
                if (status !== "OK" || !preds?.length) {
                    resolve(null);
                    return;
                }
                const placeId = preds[0].place_id;
                const ps = new gm.places.PlacesService(document.createElement("div"));
                ps.getDetails(
                    {
                        placeId,
                        fields: ["geometry", "address_components", "name", "formatted_address"],
                    },
                    (place, status2) => {
                        if (status2 !== "OK" || !place?.geometry?.location) {
                            resolve(null);
                            return;
                        }
                        resolve(place);
                    }
                );
            }
        );
    });

export default resolveFirstPrediction;
